import React, { ReactElement, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import Header from "../../components/appbar/Header";
import Content from "../../components/shared/Content";
import PlayedCouponReceiptDialog from "components/coupon/PlayedCouponReceiptDialog";
import PlayedCouponDialog from "components/playedCoupon/PlayedCouponDialog";
import Toast from "components/shared/Toast";
import { subscribeToBalanceWebsocketChannels } from "services/socketServices";
import setLanguage from "../../services/i18n";
import FixedBottomNavigation from "components/appbar/BottomNavigation";
import { useWindowSize } from "services/utils";
import {
  getSkinConfigRequested,
  getAuthSkinConfigRequested,
  getBannersRequested
} from "redux/config/configSlice";
import SkinLogo from "./assets/images/newerabet365.svg";
import ReceiptSkinLogo from "./assets/images/newerabet365_receipt.jpg";
import LoginLogo from "./assets/images/newerabet365_black.svg";
import AppDialog from "components/shared/AppDialog";
import CouponMobile from "components/coupon/CouponMobile";

import AppFooter from "components/appfooter/AppFooter";
import {
  getConfigRequested,
  getPalimpsestRequested
} from "redux/sport/sportSlice";
import { SPORT_CONFIG_INTERVAL_TIMING, SPORT_NAVBAR_INTERVAL_TIMING, SPORT_SKIN_CONFIG_INTERVAL_TIMING } from "../../constants"
import GameDialog from "components/shared/GameDialog";
import "./App.scss";
import { closeDialog, openDialog } from "redux/dialog/dialogSlice";
import { logoutRequested } from "redux/auth/authSlice";
import { useIdleTimeout } from "services/utils";

function App(): ReactElement {
  const dispatch = useAppDispatch();
  const [width] = useWindowSize();
  const userAuth = useAppSelector((state) => state.auth.isAuth);
  const authWallet = useAppSelector((state) => state.auth.authWallet);
  const bettingWalletId = useAppSelector((state) => state.wallet.bettingWalletId);
  const locale = useAppSelector((state) => state.auth.locale);
  const selectedEvent = useAppSelector((state) => state.live.selectedEvent);
  const defaultFirstEvent = useAppSelector((state) => state.live.defaultFirstEvent);
  const isDesktopLayout = useAppSelector(state => state.layoutToggle.isDesktopLayout)
  const token = sessionStorage.getItem("token");

  /**idle logic */
  const handleIdle = () => {
    dispatch(openDialog({ type: "idleTimer" }));
  }
  const { idleTimer } = useIdleTimeout({ onIdle: userAuth && handleIdle, idleTime: 28800, userAuth });

  const stay = () => {
    dispatch(closeDialog());
    idleTimer.reset();
  }
  const handleLogout = () => {
    dispatch(logoutRequested());
    dispatch(closeDialog());
  }

  /*********************** */

  useEffect(() => {
    setLanguage(locale);
    userAuth &&
      authWallet &&
      token &&
      subscribeToBalanceWebsocketChannels(token, authWallet.id);
    dispatch(getPalimpsestRequested());
    dispatch(getConfigRequested());
    dispatch(getBannersRequested())

    const palimpsestInterval = setInterval(() => {
      dispatch(getPalimpsestRequested());
    }, SPORT_NAVBAR_INTERVAL_TIMING);
    const configInterval = setInterval(() => {
      dispatch(getConfigRequested());
    }, SPORT_CONFIG_INTERVAL_TIMING);
    return () => {
      clearInterval(palimpsestInterval)
      clearInterval(configInterval)
    };
  }, []);

  useEffect(() => {
    if (userAuth && bettingWalletId) {
      dispatch(getAuthSkinConfigRequested(bettingWalletId))
      const interval = setInterval(() => {
        dispatch(getAuthSkinConfigRequested(bettingWalletId));
      }, SPORT_SKIN_CONFIG_INTERVAL_TIMING);
      return () => {
        clearInterval(interval)
      }
    } else if (!userAuth && !bettingWalletId) {
      dispatch(getSkinConfigRequested());
      const interval = setInterval(() => {
        dispatch(getSkinConfigRequested());
      }, SPORT_SKIN_CONFIG_INTERVAL_TIMING);
      return () => {
        clearInterval(interval)
      }
    }
  }, [userAuth, bettingWalletId]);

  useEffect(() => {
    /////// LIVE WIDGET RENDERING ///////
    const script = document.createElement("script");
    script.src = `https://livetracker.live/widgets/${locale}`;
    script.addEventListener("load", () => {
      if (selectedEvent && window.SBWidget) {
        window.SBWidget.ctm();
        window.SBWidget.ltm(selectedEvent, ".widget-here");
      } else if (defaultFirstEvent && window.SBWidget) {
        window.SBWidget.ctm();
        window.SBWidget.ltm(defaultFirstEvent, ".widget-here");
      }
    });
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, [locale]);

  useEffect(() => {
    const meta = document.getElementsByTagName("meta")[2];
    if (isDesktopLayout) {
      meta.removeAttribute("content");
      meta.setAttribute("content", "width=1500, initial-scale=0.1");
    } else {
      meta.removeAttribute("content");
      meta.setAttribute("content", "width=device-width, initial-scale=1");
    }
  }, [isDesktopLayout])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Grid container spacing={0}>
        <Grid item xs={12} md={12}>
          <Header skinLogo={SkinLogo} />
        </Grid>
        <Grid item xs={12} md={12}>
          <Content />
        </Grid>
        <AppFooter />
        <Toast />
        <CouponMobile />
        <GameDialog />
        <PlayedCouponDialog />
        <PlayedCouponReceiptDialog skinLogo={ReceiptSkinLogo} />
        <AppDialog skinLogo={LoginLogo} stay={stay} handleLogout={handleLogout} />

        {width <= 1250 && (
          <Grid item xs={12} md={12}>
            <FixedBottomNavigation />
          </Grid>
        )}
      </Grid>
    </ThemeProvider>
  );
}

export default App;
